import { createStore, produce } from "solid-js/store";
import { useWebSocket } from "~/modules/shared/utils/useWebSocket";

export type QuestionStep = {
	title: string;
	/** 0 to 1; null while the step has no measurable progress. */
	percentage: number | null;
};

type StatusUpdate = {
	id: string;
	status: string;
	progress?: QuestionStep | null;
};

/**
 * Questions that are running now, by public id. An entry exists exactly while
 * the question runs, so a missing id means "not running or not known".
 * Module-level so every list on a page reads the same state from one feed.
 */
const [running, setRunning] = createStore<Record<string, QuestionStep>>({});

const NO_STEP: QuestionStep = { title: "", percentage: null };

/**
 * Questions already seeded or pushed. Page data is a snapshot from page load,
 * and a sidebar that remounts re-reads it: seeding twice would bring back the
 * spinner of a question whose finish push already cleared it.
 */
const seen = new Set<string>();

/** Rows from the server carry `progress` only while their question is running. */
export function seedQuestionActivity(
	rows: ReadonlyArray<{ id: string; progress?: QuestionStep | null }>,
) {
	for (const row of rows) {
		if (seen.has(row.id)) {
			continue;
		}
		seen.add(row.id);
		if (row.progress) {
			setRunning(row.id, row.progress);
		}
	}
}

/** Ids of the questions running now. Tracks only which ids are present, not their steps. */
export const runningQuestionIds = (): string[] => Object.keys(running);

/** The step a question is on, or undefined when it is not running. */
export const questionActivity = (id: string): QuestionStep | undefined =>
	running[id];

export function applyQuestionStatus(update: StatusUpdate) {
	seen.add(update.id);
	if (update.status === "running") {
		setRunning(update.id, update.progress ?? running[update.id] ?? NO_STEP);
		return;
	}
	setRunning(
		produce((state) => {
			delete state[update.id];
		}),
	);
}

/**
 * Keeps the store live from the asker's own channel, which also carries
 * consumer and agency questions that no influencer channel hears. Call from
 * any list: the channel is shared, so the cost is one subscription however many
 * lists read the store. Pushes missed while offline are not replayed; a page reload
 * reseeds from the server.
 */
export function useQuestionActivityFeed() {
	const userId = window.pageVars?.activityUserId;
	useWebSocket<StatusUpdate>({
		channel: userId ? `users.${userId}` : undefined,
		event: ".question.status-changed",
		onMessage: applyQuestionStatus,
	});
}
